import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { TextField, Button } from '@mui/material';
import { sandboxApi } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import ShareIcon from '@mui/icons-material/Share'
import GenerationLoader from '../GenerationLoader';
import ShareDialog from '../common/ShareDialog';
import { ENTITY_TYPES } from '../../services/shareService';
import html2canvas from 'html2canvas';

const SandboxHeader = ({ token, userInfo, sandboxName, showRecreateButton, sandboxId, stageRef }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(sandboxName || '');
  const [prompt, setPrompt] = useState('');
  const [ProcessId, setProcessId] = useState(null);
  const [SandboxId, setSandboxId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const handleStartGeneration = async () => {
    try {
      const response = await sandboxApi.startProcess(
          prompt,
          userInfo.id,
          'reassembly',
          sandboxId,
          null, // aiTutorId
          token
      );
      setProcessId(response.process_id);
      setSandboxId(response.sandbox_id);
      setIsLoading(true);
    } catch (error) {
      console.error('Error recreating system:', error);
    }
    setOpen(false);
  };

  const handleNameEdit = () => {
    setIsEditing(true);
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  const handleNameSave = async () => {
    try {
      await sandboxApi.updateSandboxName(sandboxId, name, token);
      setIsEditing(false);
    } catch (error) {
      console.error(t('sandbox_header.error_updating_name'), error);
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      const templateData = await sandboxApi.createEnvironmentTemplate(sandboxId, token);
      if (templateData && templateData.process_id) {
        setProcessId(templateData.process_id);
        setSandboxId(templateData.sandbox_id);
        setIsLoading(true);
      }
    } catch (error) {
      console.error(t('sandbox_header.error_creating_template'), error);
    } finally {
      setIsLoading(false);
    }
  };

  // Функция для скачивания схемы
  const handleDownloadDiagram = async () => {
    if (stageRef && stageRef.current) {
      try {
        let dataURL;

        // Попытка использовать Konva Stage
        if (typeof stageRef.current.getStage === 'function') {
          const stage = stageRef.current.getStage();
          dataURL = stage.toDataURL({
            pixelRatio: 2,
            mimeType: 'image/png'
          });
        }
        // Если это не Konva Stage, используем html2canvas
        else {
          const canvas = await html2canvas(stageRef.current, {
            scale: 2,
            useCORS: true,
            logging: false
          });
          dataURL = canvas.toDataURL('image/png');
        }

        // Создаем ссылку для скачивания
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `${name}_diagram.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(t('sandbox_header.error_creating_image'), error);
      }
    } else {
      console.error('stageRef не определен или не имеет свойства current');
    }
  };

  // Если идет процесс генерации, показываем лоадер
  if (isLoading && ProcessId && SandboxId) {
    return <GenerationLoader token={token} ProcessId={ProcessId} SandboxId={SandboxId} />;
  }

  return (
      <div className="bg-gray-100 flex justify-between items-start w-full py-2 px-4 shadow-md">
        <div className="flex items-center">
          {isEditing ? (
              <div className="flex items-center">
                <TextField
                    value={name}
                    onChange={handleNameChange}
                    variant="outlined"
                    size="small"
                    sx={{ marginRight: '8px' }}
                />
                <Button variant="contained" color="primary" onClick={handleNameSave}>
                  {t('sandbox_header.save')}
                </Button>
              </div>
          ) : (
              <h2 className="text-xl">{name}</h2>
          )}
          <IconButton onClick={handleNameEdit} size="small" sx={{ marginLeft: '8px' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </div>

        {isLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-100">
              <div className="bg-white p-4 rounded">
                <p>{t('sandbox_header.creating_template')}</p>
              </div>
            </div>
        )}

        {/* Ссылка для скачивания схемы */}
        <div className="flex items-center space-x-4">
          <Button
              variant="text"
              color="primary"
              onClick={handleDownloadDiagram}
              sx={{ textTransform: 'none', fontSize: '0.9rem' }}
          >
            {t('sandbox_header.download_diagram')}
          </Button>

          {showRecreateButton && (
              <>
                <button
                    className="bg-orange-500 text-white py-2 px-2 rounded hover:bg-orange-600"
                    onClick={handleClickOpen}
                >
                  {t('sandbox_header.recreate')}
                </button>
                <button
                    className="bg-blue-500 text-white py-2 px-2 rounded hover:bg-blue-600"
                    onClick={handleSaveAsTemplate}
                >
                  {t('sandbox_header.save_as_template')}
                </button>
                <button
                    className="bg-green-500 text-white py-2 px-2 rounded hover:bg-green-600 flex items-center gap-2"
                    onClick={handleShareClick}
                >
                  <ShareIcon fontSize="small"/>
                  {t('sandbox_header.share')}
                </button>
              </>
          )}
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogContent>
            <h2 className="text-lg font-bold mb-4">{t('sandbox_header.enter_prompt')}</h2>
            <TextField
                multiline
                rows={5}
                fullWidth
                variant="outlined"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('sandbox_header.prompt_placeholder')}
            />
            <Button
                variant="contained"
                color="primary"
                onClick={handleStartGeneration}
                className="mt-4"
                style={{ marginTop: '20px' }}
            >
              {t('sandbox_header.start_generation')}
            </Button>
          </DialogContent>
        </Dialog>
        <ShareDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            token={token}
            entity={{ id: sandboxId, name }}
            entityType={ENTITY_TYPES.SANDBOX}
            onError={(error) => console.error(error)}
        />
      </div>
  );
};

export default SandboxHeader;
